const lower = (v) => (v || "").toString().toLowerCase();

export const toTitle = (v) =>
  lower(v)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * City for a scan. Prefers an explicit city field from the API; otherwise
 * derives it from the hub name, e.g. "BARODA-OLD PADRA ROAD" -> "Baroda".
 */
export const getCityName = (status, knownCities = []) => {
  const explicit =
    status?.city ||
    status?.locationCity ||
    status?.hubCity ||
    (typeof status?.location === "object" ? status.location?.city : null);
  if (explicit) return toTitle(explicit);

  const raw = typeof status?.location === "string" ? status.location.trim() : "";
  if (!raw) return null;

  // e.g. "KOLKATA TOLLYGUNGE" -> "Kolkata" when Kolkata is a known city
  const known = knownCities
    .filter(Boolean)
    .find((c) => lower(raw).startsWith(lower(c).trim()));
  if (known) return toTitle(known.trim());

  const city = raw
    .split(/\s*[-,(/]\s*/)[0]
    .replace(/\b(hub|gateway|branch|dc|cp|ecom|office|centre|center)\b/gi, "")
    .trim();
  return toTitle(city || raw);
};

export const STAGES = ["Booked", "In Transit", "Out for Delivery", "Delivered"];

/** "Bag Open" / "BAG_OPEN" / "bag-open" -> "bag_open" */
const norm = (v) => lower(v).trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

/*
 * Every status the backend can send (GET /tracking/statuses), with the label
 * a customer sees and what it means ("kind"):
 *   hidden    internal only, never shown
 *   booked    order / booking stage            pickup   pickup not done yet
 *   picked    picked up (moves bar to In Transit)
 *   movement  hub / CP scans, shown as one "In Transit" row per city
 *   transit   in-transit milestone shown as its own row
 *   ofd, delivered, attempted (undelivered)
 *   delay     delivery moved to a later day    hold     on hold
 *   problem   damaged / lost / pickup failed / order issue
 *   cancelled, rto (return in progress), rto_done (returned), info (no effect)
 */
const S = (label, kind) => ({ label, kind });
const MOVEMENT = S("In Transit", "movement");
const STATUS_INFO = {
  // Order / booking
  order_placed: S("Order Placed", "booked"),
  accept: S("Order Placed", "booked"),
  order_confirmed: S("Order Confirmed", "booked"),
  ready_for_dispatch: S("Ready For Dispatch", "booked"),
  error_order: S("Order Issue", "problem"),
  // Pickup
  pickup_scheduled: S("Pickup Scheduled", "pickup"),
  pickup_awaited: S("Pickup Pending", "pickup"),
  pickup_pending: S("Pickup Pending", "pickup"),
  pickup_rescheduled: S("Pickup Rescheduled", "pickup"),
  reschedule: S("Pickup Rescheduled", "pickup"),
  not_picked_up: S("Pickup Failed", "problem"),
  pickup_cancelled: S("Pickup Cancelled", "cancelled"),
  pickup_completed: S("Picked Up", "picked"),
  pickedup: S("Picked Up", "picked"),
  // Movement (category IN_TRANSIT)
  in_transit: MOVEMENT,
  departed: MOVEMENT,
  arrived: MOVEMENT,
  reachedathub: MOVEMENT,
  intransittohub: MOVEMENT,
  inscan_at_hub: MOVEMENT,
  inscanned_at_cp: MOVEMENT,
  outscan_at_hub: MOVEMENT,
  outscan_to_cp: MOVEMENT,
  outscan_to_3pl: MOVEMENT,
  outscanned_by_cp: MOVEMENT,
  added_to_bag: MOVEMENT,
  bag_open: MOVEMENT,
  bag_close: MOVEMENT,
  item_dispatched: MOVEMENT,
  item_redirect: MOVEMENT,
  item_return: MOVEMENT,
  clearance_completed: MOVEMENT,
  // Delivery
  ready_for_delivery: S("Ready For Delivery", "transit"),
  out_for_delivery: S("Out For Delivery", "ofd"),
  delivered: S("Delivered", "delivered"),
  undelivered: S("Delivery Attempted", "attempted"),
  carry_forward_to_next_day: S("Delivery Rescheduled", "delay"),
  // Problems
  on_hold: S("On Hold", "hold"),
  item_on_hold: S("On Hold", "hold"),
  damaged: S("Shipment Damaged", "problem"),
  lost: S("Shipment Lost", "problem"),
  cancelled: S("Cancelled", "cancelled"),
  // Returns / RTO
  rto: S("Return Initiated", "rto"),
  rto_initiated: S("Return Initiated", "rto"),
  rto_requested: S("Return Requested", "rto"),
  return: S("Return Initiated", "rto"),
  rto_in_transit: S("Return In Transit", "rto"),
  rto_out_for_delivery: S("Return Out For Delivery", "rto"),
  rto_undelivered: S("Return Attempt Failed", "rto"),
  rto_delivered: S("Returned To Sender", "rto_done"),
  return_delivered: S("Returned To Sender", "rto_done"),
  rto_revoked: S("Return Cancelled", "info"),
  return_revoked: S("Return Cancelled", "info"),
  // Internal only
  exception: S("", "hidden"),
  cod_reconcile: S("", "hidden"),
  pod_reconcile: S("", "hidden"),
  softdata_update: S("", "hidden"),
  consignment_verification: S("", "hidden"),
};

/** Label + kind for a status; falls back to its category, then to its name. */
export const getStatusInfo = (s) => {
  const key = norm(s?.status);
  if (STATUS_INFO[key]) return STATUS_INFO[key];
  const cat = STATUS_INFO[norm(s?.category)];
  if (cat && cat.kind !== "hidden") return cat;
  // Statuses not in the list (older or new partner codes)
  if (/inscan|outscan|arrived|depart|connected|bag|manifest|in_transit/.test(key)) return MOVEMENT;
  if (/(^|_)delivered$/.test(key) && !/rto|undelivered/.test(key)) return STATUS_INFO.delivered;
  return S(toTitle(key) || "Status Updated", "info");
};

const kindOf = (s) => getStatusInfo(s).kind;
const isDelivered = (s) => kindOf(s) === "delivered";
export const isHiddenStatus = (s) => kindOf(s) === "hidden";
export const isCancelled = (s) => kindOf(s) === "cancelled";

/** Index into STAGES of the furthest stage the shipment has reached. */
export const getStageIndex = (statuses = []) => {
  let stage = 0;
  statuses.forEach((s) => {
    const kind = kindOf(s);
    if (kind === "delivered") stage = Math.max(stage, 3);
    else if (kind === "ofd") stage = Math.max(stage, 2);
    // Only real movement moves the bar; cancelled / pickup / RTO steps do not.
    else if (["movement", "transit", "picked"].includes(kind)) stage = Math.max(stage, 1);
  });
  return stage;
};

/** Customer-facing label for a scan. */
export const getDisplayLabel = (s) => s?.__label || getStatusInfo(s).label;

const TRANSIT_LABEL = "In Transit";
/** Internal hub / CP movement scans (inscan, outscan, bagging...). */
export const isMovementScan = (s) => kindOf(s) === "movement";

// Events that may repeat (attempts, holds, returns) keep every occurrence.
const REPEATABLE = ["ofd", "attempted", "delivered", "delay", "hold", "problem", "rto", "rto_done"];
const isAttempt = (s) => REPEATABLE.includes(kindOf(s));

/** Position in the shipment lifecycle; higher = later. */
const RANK = {
  cancelled: 8, rto_done: 7.5, rto: 7, delivered: 6, attempted: 5, delay: 5,
  hold: 4.5, problem: 4.5, ofd: 4, transit: 3.8, movement: 3.5, picked: 2, pickup: 1.5,
};
const lifecycleRank = (s) => {
  if (norm(s?.status) === "ready_for_dispatch") return 1;
  return RANK[kindOf(s)] ?? 0; // booked / info
};

const tsValue = (ts) => {
  const n = typeof ts === "string" ? parseInt(ts, 10) : ts;
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Customer timeline, newest first. Internal scans (hub / CP) are kept but
 * shown with customer-friendly labels; only back-to-back repeats of the
 * same label in the same city are collapsed into the latest one.
 */
export const sortNewestFirst = (statuses = []) => {
  const list = [...statuses];
  if (!list.every((s) => tsValue(s.statusTimestamp))) return list; // keep API order
  return list.sort((a, b) => {
    // Compare to the second; events logged in the same second are
    // ordered by where they fall in the shipment lifecycle.
    const diff =
      Math.floor(tsValue(b.statusTimestamp) / 1000) -
      Math.floor(tsValue(a.statusTimestamp) / 1000);
    return diff !== 0 ? diff : lifecycleRank(b) - lifecycleRank(a);
  });
};

export const getTimeline = (statuses = [], knownCities = []) => {
  const sorted = sortNewestFirst(statuses.filter((s) => !isHiddenStatus(s)));

  // Internal hub / CP movement scans (inscan, outscan, connected, ...) are
  // shown to customers as a single "In transit" row per city: consecutive
  // scans in the same city collapse into the latest one, so the journey shows
  // where the shipment currently is without listing every hub scan.
  const list = [];
  sorted.forEach((s) => {
    if (!isMovementScan(s)) {
      list.push(s);
      return;
    }
    const city = getCityName(s, knownCities) || "";
    const prev = list[list.length - 1];
    if (prev?.__label === TRANSIT_LABEL && prev.__city === city) return; // same city, older scan
    list.push({ ...s, __label: TRANSIT_LABEL, __city: city });
  });

  // One-time milestones (e.g. Ready for dispatch logged twice) show only their
  // latest entry; delivery attempts and transit rows keep every occurrence.
  const seen = new Set();
  return list.filter((s) => {
    if (isAttempt(s) || s.__label === TRANSIT_LABEL) return true;
    const label = getDisplayLabel(s);
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });
};


/**
 * City shown for a timeline row. READY_FOR_DISPATCH carries an internal
 * location, so it uses the origin city, falling back to the nearest scan.
 */
export const getRowCity = (timeline, idx, { sourceCity, knownCities = [] }) => {
  const status = timeline[idx];
  if (norm(status?.status) !== "ready_for_dispatch") {
    return getCityName(status, knownCities);
  }
  if (sourceCity) return toTitle(sourceCity);
  for (let d = 1; d < timeline.length; d++) {
    const city =
      getCityName(timeline[idx + d], knownCities) ||
      getCityName(timeline[idx - d], knownCities);
    if (city) return city;
  }
  return null;
};

/**
 * Reason shown under an undelivered / RTO / on-hold milestone, taken from the
 * scan's `event` (e.g. "CONSIGNEE NOT AVAILABLE" -> "Consignee not available").
 */
export const getExceptionReason = (s) => {
  const reason = (s?.event || "").toString().replace(/_/g, " ").trim();
  if (!reason || !isExceptionStatus(s)) return null;
  // Skip when the "reason" just repeats the status itself
  if (norm(reason) === norm(s?.status)) return null;
  return toSentence(reason);
};

/** Rows that need the customer's attention are highlighted and show a reason. */
const ALERT_KINDS = ["attempted", "delay", "hold", "problem", "cancelled", "rto", "rto_done"];
export const isExceptionStatus = (s) => ALERT_KINDS.includes(kindOf(s));

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const toDate = (ts) => {
  const n = tsValue(ts);
  if (!n) return null;
  const d = new Date(n);
  return isNaN(d.getTime()) ? null : d;
};

/** e.g. "03 Oct 2026" */
export const formatDate = (ts) => {
  const d = toDate(ts);
  if (!d) return "-";
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/** e.g. "4:44 PM" */
export const formatTime = (ts) => {
  const d = toDate(ts);
  if (!d) return "-";
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${d.getHours() % 12 || 12}:${minutes} ${d.getHours() >= 12 ? "PM" : "AM"}`;
};

/** POD link to show: the first POD link, if any. */
export const getPodLink = (info) => info?.pod_links?.[0] || null;

/**
 * True when the shipment's latest status is Delivered (original rule:
 * POD is offered only once the shipment is currently delivered).
 */
export const hasDeliveredEvent = (statuses = []) =>
  isDelivered(sortNewestFirst(statuses.filter((s) => !isHiddenStatus(s)))[0]);

/** "Out For Delivery" -> "Out for delivery" */
export const toSentence = (v) => {
  const t = lower(v);
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** "Today · 03 Oct", "Yesterday · 02 Oct", "01 Oct" (year added if not this year) */
export const formatDayLabel = (ts, now = new Date()) => {
  const d = toDate(ts);
  if (!d) return "Date unavailable";
  const base = `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]}`;
  const withYear = d.getFullYear() === now.getFullYear() ? base : `${base} ${d.getFullYear()}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(d, now)) return `Today · ${base}`;
  if (isSameDay(d, yesterday)) return `Yesterday · ${base}`;
  return withYear;
};

export const isToday = (ts, now = new Date()) => {
  const d = toDate(ts);
  return !!d && isSameDay(d, now);
};

/**
 * Overall shipment state from the latest customer-facing milestone.
 * tone drives colours: "delivered" | "active" | "alert" | "neutral".
 */
export const getShipmentSummary = (statuses = [], knownCities = []) => {
  const visible = statuses.filter((s) => !isHiddenStatus(s));
  const all = sortNewestFirst(visible);
  const timeline = getTimeline(visible, knownCities);
  const latest = timeline[0] || all[0] || null;
  const stage = getStageIndex(visible);
  const kind = kindOf(latest);
  const label = latest ? getDisplayLabel(latest) : "";

  const byKind = {
    cancelled: "cancelled",
    rto: "rto",
    rto_done: "rto",
    delivered: "delivered",
    attempted: "attempted",
    delay: "delay",
    hold: "hold",
    problem: "issue",
    ofd: "ofd",
  };
  const state = byKind[kind] || (stage >= 1 ? "transit" : "booked");

  const headlines = {
    delivered: "Delivered",
    ofd: isToday(latest?.statusTimestamp) ? "Arriving today" : "Out for delivery",
    attempted: "Delivery attempted",
    delay: "Delivery rescheduled",
    hold: "Shipment on hold",
    issue: toSentence(label),
    rto: kind === "rto_done" ? "Returned to sender" : "Returning to sender",
    transit: "In transit",
    booked: kind === "pickup" ? toSentence(label) : "Order confirmed",
    cancelled: kind === "cancelled" && /pickup/.test(norm(latest?.status))
      ? "Pickup cancelled"
      : "Order cancelled",
  };
  const tones = {
    delivered: "delivered",
    ofd: "active",
    transit: "neutral",
    booked: "neutral",
  };

  return {
    state,
    tone: tones[state] || "alert",
    headline: headlines[state],
    badge: label,
    latest,
    stage,
    timeline,
    bookedAt: all[all.length - 1]?.statusTimestamp,
    deliveredAt: state === "delivered" ? latest?.statusTimestamp : null,
  };
};

/**
 * Courier bookings (orderInformation.type === "COURIER") say "Shipment confirmed";
 * other bookings (e-commerce) keep "Order confirmed".
 */
export const applyBookingLabels = (statuses = [], bookingType) => {
  if (lower(bookingType) !== "courier") return statuses;
  return statuses.map((s) =>
    norm(s?.status) === "order_confirmed" ? { ...s, __label: "Shipment Confirmed" } : s
  );
};
